import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import PUBLIC from "@/data/gatePublic.json";
import { getWeeks } from "@/lib/journey";
import { Eyebrow, Reveal } from "./Reveal";

const GATE = 4;
const protocol = PUBLIC.sample?.sections?.find((s) => /protocol/i.test(s.h))?.items ?? [];
const STEPS = protocol.reduce((acc, it) => {
  if (it.k === "h3") acc.push({ t: it.t.replace(/^Step \d+:\s*/, ""), d: [] });
  else if (acc.length) acc[acc.length - 1].d.push(it);
  return acc;
}, []);
const weeks = getWeeks(GATE);

export default function Ritual() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 70%", "end 60%"] });
  const h = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.15, 1]);

  return (
    <section id="ritual" data-testid="ritual-section" className="relative max-w-[1440px] mx-auto px-5 sm:px-10 py-28 sm:py-40">
      <div className="grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-28">
            <Reveal>
              <Eyebrow n="V">Sample Ritual · Gate 5</Eyebrow>
              <div className="mt-8 flex items-center gap-4 flex-wrap">
                <span data-testid="ritual-sample-badge" className="rounded-full border border-[#C2414F] bg-[#5A0F1A]/40 px-4 py-1.5 font-tt-mono text-[12px] uppercase tracking-[0.3em] text-[#F2E8DF]">Sample</span>
                <h2 data-testid="ritual-title" className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
                  Breath<em className="text-[#D9B47A]">wave</em>
                </h2>
              </div>
              <p className="mt-4 font-display italic text-xl text-[#D9B47A]">Goddess Ariel’s Signature Ankhing Breath</p>
              <p className="mt-5 text-base sm:text-lg text-[#A8998C] leading-relaxed">
                The body as current. Breath becomes a conveyor of bliss — circulated from perineum to spine, heart and
                crown, until pleasure moves as a continuous, undulating wave through the whole body.
              </p>
            </Reveal>
            <div className="mt-10 arch overflow-hidden aspect-[4/5] max-w-[380px] border border-[#D9B47A]/20">
              <motion.img style={{ scale: imgScale }} src="/images/breath.jpg" alt="A woman in profile exhaling with eyes closed, half in shadow" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>

        <div className="lg:col-span-7 relative">
          <div ref={ref} className="relative pl-10 sm:pl-14">
            <span className="absolute left-[7px] top-0 bottom-0 w-px bg-[#D9B47A]/15" />
            <motion.span style={{ height: h }} className="absolute left-[7px] top-0 w-px bg-[#C2414F]" />
            {STEPS.map((s, i) => (
              <Reveal key={s.t} className="relative pb-16" data-testid={`ritual-step-${i + 1}`}>
                <span className="absolute -left-10 sm:-left-14 top-2 w-[15px] h-[15px] rounded-full border border-[#D9B47A] bg-[#0A0706]" />
                <p className="font-tt-mono text-[12px] uppercase tracking-[0.3em] text-[#D9B47A]">{i === 0 ? "Begin" : `Step ${i}`}</p>
                <h3 className="font-display text-3xl sm:text-4xl mt-3">{s.t}</h3>
                {s.d.map((it, k) =>
                  it.k === "label" ? (
                    <p key={k} className="mt-5 font-tt-mono text-[12px] uppercase tracking-[0.25em] text-[#C2414F]">{it.t.replace(/:$/, "")}</p>
                  ) : (
                    <p key={k} className="mt-3 text-base sm:text-lg text-[#CFC2B6] leading-relaxed max-w-2xl">{it.t}</p>
                  )
                )}
              </Reveal>
            ))}
          </div>

          <Reveal className="grid sm:grid-cols-3 gap-4 mt-4">
            {weeks.map((w, k) => (
              <div key={w.title} className="border border-[#D9B47A]/20 p-6 hover:border-[#D9B47A]/60 transition-colors duration-500">
                <p className="font-tt-mono text-[12px] uppercase tracking-[0.25em] text-[#A8998C]">Week {k + 1}</p>
                <p className="font-display text-2xl mt-3">{w.name}</p>
                <p className="text-sm text-[#D9B47A] mt-2">{w.duration}</p>
              </div>
            ))}
          </Reveal>
          <Reveal className="mt-14">
            <p className="font-display italic text-3xl sm:text-4xl text-[#F2E8DF]" data-testid="ritual-affirmation">
              “I am breath made radiant.”
            </p>
            <p className="mt-4 text-sm text-[#A8998C] max-w-xl">
              With a partner, consent and communication remain essential. “Do you want to synchronize our breath or our release?”
            </p>
            <Link
              to="/gates/breathwave"
              data-testid="ritual-full-guide-link"
              className="group mt-10 inline-flex items-center gap-3 rounded-full border border-[#D9B47A]/60 px-6 py-3 font-tt-mono text-[12px] uppercase tracking-[0.2em] hover:bg-[#D9B47A] hover:text-[#0A0706] transition-colors"
            >
              Open the full Breathwave guide <ArrowUpRight className="w-4 h-4 transition-transform duration-500 group-hover:rotate-45" />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
